import { Menu, rem } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArticle, IconRotateClockwise, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Can } from "#modules/company/view/Can";
import { getPartsFromFiles } from "#modules/parts/domain/getPartsFromFiles";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { useUpdateMaterial } from "#modules/quoteRequestLines/useUpdateMaterial";
import { ValidateMenuItem } from "#modules/quoteRequestLines/ValidateMenuItem";
import { useAdd2dFile } from "#modules/quoteRequestLines/view/useAdd2dFile";
import { useDeleteQuoteRequestLineOrBatch } from "#modules/quoteRequestLines/view/useDeleteQuoteRequestLineOrBatch";
import { useDuplicateQuoteRequestLine } from "#modules/quoteRequestLines/view/useDuplicateQuoteRequestLine";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { useCheckPart } from "#modules/similarPart/view/useCheckPart";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Anchor } from "#shared/view/components/Anchor";
import {
  getFirst2DFile,
  get3DFile,
  UPLOAD_CONFIG,
  VALID_2D_FILES_EXTENSIONS,
  is2DFile,
  formatFileSize,
} from "shared";
import { AssemblyWithChildren } from "shared";

import { CreateBatchDisclaimerModal } from "./CreateBatchDisclaimerModal";
import { invalidateQuoteRequestLinesDebounced } from "./infra/invalidateQuoteRequestLinesDebounced";
import { useBatchDisclaimerModal } from "./useBatchDisclaimerModal";

type Props = {
  openDetailsPanelForQuoteRequestLine: (quoteRequestLineId: string) => void;
  part: PartDto | undefined | null;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  quoteRequestLine: QuoteRequestLineDto;
  isDetailsPanelDisplayable: boolean;
};

export const QuoteRequestLineActionsMenu = ({
  quoteRequestLine,
  openDetailsPanelForQuoteRequestLine,
  part,
  assembliesInBatch,
  isDetailsPanelDisplayable,
}: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { assemblies, setActiveAssemblyForBatch } = useAssembliesContext();
  const { duplicateQuoteRequestLine } = useDuplicateQuoteRequestLine();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDeleteSuccess = (deletedIds?: string[]) => {
    const isLastLine = assemblies.length === 1 && assemblies[0]!.children.length === 1;
    const isLastBatch = deletedIds && assemblies.length === deletedIds.length;

    if (isLastLine || isLastBatch) {
      navigate(ROUTES.partner);
    }
  };

  const { deleteQuoteRequestLineOrBatch } = useDeleteQuoteRequestLineOrBatch({
    onSuccess: onDeleteSuccess,
  });
  const { updateMaterial } = useUpdateMaterial({ trigger: "rerunPrediction" });

  const { checkPart: reanalyzePart, isPending: isReanalyzing } = useCheckPart({
    onSuccess: () => {
      invalidateQuoteRequestLinesDebounced({
        queryClient,
        quoteRequestLineId: quoteRequestLine.id,
      });
    },
  });

  const { add2dFile, isPending: isAdd2dFileLoading } = useAdd2dFile({
    onSuccess: () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    onError: () => {
      notifications.show({
        title: t("quoteRequestLine.actions.upload2DDrawing.error"),
        message: t("quoteRequestLine.actions.upload2DDrawing.errorMessage"),
        color: "red",
      });
    },
  });

  const materialGrade = quoteRequestLine.materialGrade;
  const materialName = quoteRequestLine.materialName;
  const file3D = get3DFile(part?.files);
  const file2D = getFirst2DFile(part?.files ?? []);
  const fileUrl = file3D?.url ?? file2D?.url ?? undefined;
  const isAlreadyABatch = assembliesInBatch.length > 1;
  const quoteRequestLineIdsInBatch = assembliesInBatch.map((a) => a.quoteRequestLine.id);

  const { shouldShowModal, modalOpened, openModal, closeModal, handleConfirm, checkboxProps } =
    useBatchDisclaimerModal();

  const batchButtonLabel = isAlreadyABatch
    ? t("quoteRequestLine.actions.addBatch")
    : t("quoteRequestLine.actions.createBatch");

  const handleDuplicate = () => {
    duplicateQuoteRequestLine({
      id: quoteRequestLine.id,
      useBatch: false,
    });
  };

  const handleAddToBatch = () => {
    duplicateQuoteRequestLine(
      {
        id: quoteRequestLine.id,
        useBatch: true,
      },
      {
        onSuccess: (data) => {
          if (data) {
            setActiveAssemblyForBatch(data.partId, data.id);
          }
        },
      },
    );
  };

  const handleBatchMenuClick = () => {
    if (shouldShowModal(isAlreadyABatch)) {
      openModal();
    } else {
      handleAddToBatch();
    }
  };

  const handleBatchModalConfirm = () => {
    handleConfirm(handleAddToBatch);
  };

  const handleDelete = () => {
    deleteQuoteRequestLineOrBatch(quoteRequestLineIdsInBatch, quoteRequestLine.id);
  };

  const openDetailsPanel = () => {
    openDetailsPanelForQuoteRequestLine(quoteRequestLine.id);
  };

  const rerunPrediction = async () => {
    await updateMaterial({
      quoteRequestLineId: quoteRequestLine.id,
      predictionStatus: quoteRequestLine.predictionStatus,
      payload: {
        materialName,
        materialGrade,
      },
    });
  };

  const handleReanalyze = () => {
    if (!part?.id) {
      return;
    }

    reanalyzePart({
      id: part.id,
      force: true,
      quoteRequestLineId: quoteRequestLine.id,
    });
  };

  const handleUpload2DDrawing = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !part?.id) {
      return;
    }

    const [partData] = await getPartsFromFiles([file]);

    if (!partData || !partData.files[0]) {
      return;
    }

    if (partData.files.some((f) => f.data.size > UPLOAD_CONFIG.PART.maxFileSize)) {
      notifications.show({
        title: t("quoteRequestLine.actions.upload2DDrawing.error"),
        message: t("common.fileTooLarge.exceedsMaxSize", {
          maxSize: formatFileSize(UPLOAD_CONFIG.PART.maxFileSize),
        }),
        color: "red",
      });
      return;
    }

    add2dFile({
      data: {
        partId: part.id,
        file: partData.files[0],
        quoteRequestLineId: quoteRequestLine.id,
      },
    });
  };

  return (
    <>
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <ActionIcon size="lg" aria-label="More actions">
            <Icon name="moreHorizontal" size={24} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconArticle style={{ width: rem(14), height: rem(14) }} />}
            onClick={openDetailsPanel}
            disabled={!isDetailsPanelDisplayable}
          >
            {t("quoteRequestLine.actions.detail")}
          </Menu.Item>

          <ValidateMenuItem quoteRequestLineId={quoteRequestLine.id} />

          <Menu.Item
            leftSection={<Icon name="refresh" size={14} />}
            onClick={rerunPrediction}
            disabled={!materialGrade}
          >
            {t("quoteRequestLine.actions.rerunPrediction")}
          </Menu.Item>

          <Menu.Item
            leftSection={<IconRotateClockwise style={{ width: rem(14), height: rem(14) }} />}
            onClick={handleReanalyze}
            disabled={!part?.id || isReanalyzing}
          >
            {t("quoteRequestLine.actions.reanalyze")}
          </Menu.Item>

          <Menu.Item leftSection={<Icon name="download" size={14} />} disabled={!fileUrl}>
            <Anchor
              href={fileUrl}
              style={{ textDecoration: "none" }}
              component="a"
              c="inherit"
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              {t("quoteRequestLine.actions.download")}
            </Anchor>
          </Menu.Item>

          <Can feature="twoD">
            <Menu.Item
              leftSection={<Icon name="upload" size={14} />}
              onClick={handleUpload2DDrawing}
              disabled={!part?.id || isAdd2dFileLoading || part.files.some(is2DFile)}
            >
              {t("quoteRequestLine.actions.upload2DDrawing.button")}
            </Menu.Item>
          </Can>
          <div style={{ cursor: "pointer" }}>
            <Menu.Item
              leftSection={<Icon name={isAlreadyABatch ? "layerPlus" : "layer"} size={14} />}
              onClick={handleBatchMenuClick}
            >
              {batchButtonLabel}
            </Menu.Item>
          </div>
          <div style={{ cursor: "pointer" }}>
            <Menu.Item leftSection={<Icon name="duplicate" size={14} />} onClick={handleDuplicate}>
              {t("quoteRequestLine.actions.duplicate.button")}
            </Menu.Item>
          </div>

          <Menu.Item
            color="red"
            leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
            onClick={handleDelete}
          >
            {t("quoteRequestLine.actions.deletePart")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <CreateBatchDisclaimerModal
        opened={modalOpened}
        onClose={closeModal}
        onConfirm={handleBatchModalConfirm}
        checkboxProps={checkboxProps}
        confirmLabel={batchButtonLabel}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={VALID_2D_FILES_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
        data-testid="add-2d-drawing-file-input"
      />
    </>
  );
};
